import {
  Text,
  Modal,
  Button,
  Heading,
  useToast,
  ModalBody,
  ButtonGroup,
  ModalFooter,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  CardHeader,
  CardBody,
  Card,
  Avatar,
  Flex
} from '@homebotapp/hb-react-component-catalog'
import { REA } from '../../../constants/agent_types'
import { RootState } from '../../../types/rootState'
import { useIntl, defineMessages } from 'react-intl'
import { useAppSelector } from '../../../store/hooks'
import { useDispatch, useSelector } from 'react-redux'
import { useTrackingContext } from '../../../providers/tracking'
import { selectSponsorshipFirstName, selectSponsorshipPhotoUri } from '../../../store/selectors/sponsorship'
import { selectPrequalRequestSuccess, selectPrequalRequestFailure } from '../../../store/selectors/affordability'
import { CUSTOMER_MESSAGES, PREQUAL_TOPIC_KEYS_UI_CONTEXT, TopicKey } from '../../../constants/customerMessages'
import { capitalizeFirstLetter } from '../../homeSearch/Utils/format'
import { usePrequalRequest } from '../../../hooks/usePrequalRequest'
import { useEffect, useState } from 'react'

export const MSG = defineMessages({
  title: {
    id: 'QuestionPrequalConfirmationModal.title',
    defaultMessage: '✅ Message Sent!'
  },
  body: {
    id: 'QuestionPrequalConfirmationModal.body',
    defaultMessage: '{customerName} will be in touch soon to answer your question.'
  },
  cardTitleLo: {
    id: 'QuestionPrequalConfirmationModal.cardTitleLo',
    defaultMessage: 'In the meantime, would you like to get pre-qualified?'
  },
  cardTitleRea: {
    id: 'QuestionPrequalConfirmationModal.cardTitleRea',
    defaultMessage: 'In the meantime, would you like to get pre-qualified by my trusted lending partner, {sponsorName}?'
  },
  cardTitleRefiOrCashOut: {
    id: 'QuestionPrequalConfirmationModal.cardTitleRefiOrCashOut',
    defaultMessage: 'In the meantime, would you like to take the next step?'
  },
  cardBodyPrequal: {
    id: 'QuestionPrequalConfirmationModal.cardBody.prequal',
    defaultMessage:
      'Pre-qualifying gives you a clear budget with no obligation to proceed. {customerName} will reach out to discuss your options.'
  },
  cardBodyRefiLo: {
    id: 'QuestionPrequalConfirmationModal.cardBody.refiLo',
    defaultMessage:
      "Let {customerName} know that you're interested in refinancing so you can get the process started. No obligations to proceed at this time."
  },
  cardBodyCashOutLo: {
    id: 'QuestionPrequalConfirmationModal.cardBody.cashOutLo',
    defaultMessage:
      "Let {customerName} know that you're interested in using your equity so you can get the process started. No obligations to proceed at this time."
  },
  cardBodyRefiRea: {
    id: 'QuestionPrequalConfirmationModal.cardBody.refiRea',
    defaultMessage:
      "I'll let my trusted lending partner, {sponsorName}, know that you're interested in refinancing so you can get the process started. No obligations to proceed at this time."
  },
  cardBodyCashOutRea: {
    id: 'QuestionPrequalConfirmationModal.cardBody.cashOutRea',
    defaultMessage:
      "I'll let my trusted lending partner, {sponsorName}, know that you're interested in using your equity so you can get the process started. No obligations to proceed at this time."
  },
  cancel: {
    id: 'QuestionPrequalConfirmationModal.cancel',
    defaultMessage: 'Cancel'
  },
  getPrequalified: {
    id: 'QuestionPrequalConfirmationModal.getPrequalified',
    defaultMessage: 'Request a pre-qual'
  },
  letsDoIt: {
    id: 'QuestionPrequalConfirmationModal.letsDoIt',
    defaultMessage: "Let's do it"
  },
  prequalRequestSuccess: {
    id: 'QuestionPrequalConfirmationModal.prequalRequestSuccess',
    defaultMessage: 'Pre-qualification request sent!'
  },
  prequalRequestError: {
    id: 'QuestionPrequalConfirmationModal.prequalRequestError',
    defaultMessage: 'Failed to send pre-qualification request. Please try again.'
  },
  refiEmailMessage: {
    id: 'QuestionPrequalConfirmationModal.refiEmailMessage',
    defaultMessage: 'Can we get the refi process started?'
  },
  cashOutEmailMessage: {
    id: 'QuestionPrequalConfirmationModal.cashOutEmailMessage',
    defaultMessage: 'Can we get the equity cashout process started?'
  }
})

export interface QuestionPrequalConfirmationModalProps {
  onClose: () => void
  prequalTopicKey: TopicKey
}

export const QuestionPrequalConfirmationModal = ({
  onClose,
  prequalTopicKey
}: QuestionPrequalConfirmationModalProps) => {
  const intl = useIntl()
  const toast = useToast()
  const {
    sendPrequal,
    isLoading: isPrequalLoading,
    isSuccess: isPrequalSuccess,
    isError: isPrequalFailure
  } = usePrequalRequest()
  const sponsorName = useSelector(selectSponsorshipFirstName)
  const isRea = useAppSelector((state: RootState) => state.customerProfile.data?.type === REA)
  const customerName = useAppSelector((state: RootState) => state.customerProfile.data.firstName)
  const customerPhoto = useAppSelector((state: RootState) => state.customerProfile.data.photoUri)
  const sponsorPhotoUri = useSelector(selectSponsorshipPhotoUri)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const isRefiDm = prequalTopicKey === CUSTOMER_MESSAGES.REFINANCING.topicKey
  const isCashOutDm = prequalTopicKey === CUSTOMER_MESSAGES.USING_EQUITY.topicKey

  const refiOrCashOutDm = isRefiDm || isCashOutDm

  // Handle success state
  useEffect(() => {
    if (!isPrequalLoading && isPrequalSuccess) {
      toast({
        status: 'success',
        title: intl.formatMessage(MSG.prequalRequestSuccess)
      })
      onClose()
    }
  }, [isPrequalLoading, isPrequalSuccess, toast, intl, onClose])

  // Handle failure state
  useEffect(() => {
    if (!isPrequalLoading && isPrequalFailure) {
      toast({
        status: 'error',
        title: intl.formatMessage(MSG.prequalRequestError)
      })
    }
  }, [isPrequalLoading, isPrequalFailure, toast, intl])

  const handleSend = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    let customMessage: string | undefined
    let topic: string | undefined

    if (isRefiDm) {
      customMessage = intl.formatMessage(MSG.refiEmailMessage)
      topic = CUSTOMER_MESSAGES.REFINANCING.topic
    } else if (isCashOutDm) {
      customMessage = intl.formatMessage(MSG.cashOutEmailMessage)
      topic = CUSTOMER_MESSAGES.USING_EQUITY.topic
    }

    setIsSubmitting(true)
    // Prequals are soooo hot right now
    sendPrequal({
      topic: PREQUAL_TOPIC_KEYS_UI_CONTEXT[prequalTopicKey],
      topicKey: prequalTopicKey,
      message: customMessage
    })
  }

  const displayName = capitalizeFirstLetter(customerName)
  const hasSponsor = isRea && sponsorName
  const prequalProfessionalName = hasSponsor ? capitalizeFirstLetter(sponsorName) : capitalizeFirstLetter(customerName)

  const cardBodyMessage = isRefiDm
    ? hasSponsor
      ? intl.formatMessage(MSG.cardBodyRefiRea, { sponsorName: capitalizeFirstLetter(sponsorName) })
      : intl.formatMessage(MSG.cardBodyRefiLo, { customerName: displayName })
    : isCashOutDm
    ? hasSponsor
      ? intl.formatMessage(MSG.cardBodyCashOutRea, { sponsorName: capitalizeFirstLetter(sponsorName) })
      : intl.formatMessage(MSG.cardBodyCashOutLo, { customerName: displayName })
    : intl.formatMessage(MSG.cardBodyPrequal, { customerName: prequalProfessionalName })

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading mb={0} textAlign='left'>
            {intl.formatMessage(MSG.title)}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text size='xs'>{intl.formatMessage(MSG.body, { customerName: displayName })}</Text>
          <Card>
            <CardHeader pb={0} backgroundColor='card-bg-layer-3'>
              <Flex>
                <Flex direction={'row'} mt={10} alignItems={'center'} marginTop={0} mr={2}>
                  <Avatar src={customerPhoto} />
                  {hasSponsor && <Avatar src={sponsorPhotoUri} ml={-5} />}
                </Flex>
                <Heading textAlign='left' mb={0} size='sm'>
                  {refiOrCashOutDm
                    ? intl.formatMessage(MSG.cardTitleRefiOrCashOut)
                    : isRea
                    ? intl.formatMessage(MSG.cardTitleRea, { sponsorName })
                    : intl.formatMessage(MSG.cardTitleLo)}
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody backgroundColor='card-bg-layer-3'>
              <Text size='xs'>{cardBodyMessage}</Text>
            </CardBody>
          </Card>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup spacing={3}>
            <Button
              size='sm'
              tracking={{
                descriptor: 'Cancel',
                guid: '7VBc3gQbEHJQA6g2aYRM6q',
                ui_context: 'QuestionPrequalConfirmationModal'
              }}
              onClick={onClose}
              disabled={isSubmitting}
            >
              {intl.formatMessage(MSG.cancel)}
            </Button>
            <Button
              size='sm'
              variant='solid'
              colorScheme='primary'
              onClick={handleSend}
              isLoading={isSubmitting}
              tracking={{
                descriptor: refiOrCashOutDm ? "Let's do it" : 'Get pre-qualified',
                guid: 'ptRwMrVkhwWqc6gZXeCLFn',
                ui_context: 'QuestionPrequalConfirmationModal'
              }}
            >
              {refiOrCashOutDm ? intl.formatMessage(MSG.letsDoIt) : intl.formatMessage(MSG.getPrequalified)}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
